if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.viewerButtons = {
          prev: this.querySelector('.main-product-gallery-nav .slider-button--prev'),
          next: this.querySelector('.main-product-gallery-nav .slider-button--next'),
        };

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce((event) => {
            if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) return;
            this.onSlideChanged(event);
          }, 500)
        );
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        [this.viewerButtons.prev, this.viewerButtons.next].forEach((button) => {
          button?.addEventListener('click', this.onViewerButtonClick.bind(this));
        });
        this.mql.addEventListener('change', () => {
          this.prepareDesktopGallery();
          this.updateViewerButtons();
        });
        this.prepareDesktopGallery();
        this.updateViewerButtons();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      prepareDesktopGallery() {
        if (!this.dataset.desktopLayout.includes('thumbnail') || !this.mql.matches || !this.elements.viewer) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((slide) => {
          slide.classList.remove('scroll-trigger--offscreen', 'animate--fade-in', 'animate--slide-in');
          slide.classList.add('scroll-trigger--cancel');
          this.ensureSlideImagesVisible(slide);
        });

        const activeMedia = this.elements.viewer.querySelector('.is-active');
        if (activeMedia) {
          this.ensureSlideImagesVisible(activeMedia);
        }

        if (this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollLeft = 0;
          this.elements.viewer.slider.style.overflow = 'hidden';
        }
      }

      ensureSlideImagesVisible(slide) {
        slide.querySelectorAll('img').forEach((image) => {
          image.loading = 'eager';
          image.style.opacity = '1';
        });
      }

      onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        if (!currentElement?.dataset?.mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateViewerButtons();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        activeMedia.classList.remove('scroll-trigger--offscreen', 'animate--fade-in', 'animate--slide-in');
        activeMedia.classList.add('scroll-trigger--cancel');
        this.ensureSlideImagesVisible(activeMedia);

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches && this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollLeft = 0;
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.updateViewerButtons();
      }

      onViewerButtonClick(event) {
        if (!this.dataset.desktopLayout.includes('thumbnail') || !this.mql.matches || !this.elements.thumbnails) return;

        event.preventDefault();
        event.stopPropagation();

        const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const activeMedia = mediaItems.find((element) => element.classList.contains('is-active'));
        const activeId = activeMedia?.dataset.mediaId;
        const activeIndex = thumbnailItems.findIndex((thumbnail) => thumbnail.dataset.target === activeId);

        if (activeIndex === -1) return;

        const nextIndex = event.currentTarget.name === 'next' ? activeIndex + 1 : activeIndex - 1;

        if (nextIndex < 0 || nextIndex >= thumbnailItems.length) return;
        this.setActiveMedia(thumbnailItems[nextIndex].dataset.target, false);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        const useVerticalThumbnails =
          this.dataset.desktopLayout.includes('thumbnail') &&
          window.matchMedia('(min-width: 990px)').matches;

        if (useVerticalThumbnails && thumbnailList) {
          const listTop = thumbnailList.scrollTop;
          const listHeight = thumbnailList.clientHeight;
          const itemTop = thumbnail.offsetTop;
          const itemHeight = thumbnail.offsetHeight;

          if (itemTop < listTop) {
            thumbnailList.scrollTo({ top: itemTop, behavior: 'smooth' });
          } else if (itemTop + itemHeight > listTop + listHeight) {
            thumbnailList.scrollTo({ top: itemTop + itemHeight - listHeight, behavior: 'smooth' });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      updateViewerButtons() {
        if (!this.viewerButtons.prev || !this.viewerButtons.next) return;

        if (!this.dataset.desktopLayout.includes('thumbnail') || !this.mql.matches || !this.elements.thumbnails) {
          this.viewerButtons.prev.removeAttribute('disabled');
          this.viewerButtons.next.removeAttribute('disabled');
          return;
        }

        const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const activeMedia = mediaItems.find((element) => element.classList.contains('is-active'));
        const activeIndex = thumbnailItems.findIndex(
          (thumbnail) => thumbnail.dataset.target === activeMedia?.dataset.mediaId
        );

        if (activeIndex <= 0) {
          this.viewerButtons.prev.setAttribute('disabled', 'disabled');
        } else {
          this.viewerButtons.prev.removeAttribute('disabled');
        }

        if (activeIndex === -1 || activeIndex >= thumbnailItems.length - 1) {
          this.viewerButtons.next.setAttribute('disabled', 'disabled');
        } else {
          this.viewerButtons.next.removeAttribute('disabled');
        }
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
